import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Backdrop,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { getHotelAccounts } from '../api/adminPanelApi';

const HotelAccounts = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch hotel accounts from API
  const fetchHotelAccounts = async () => {
    setLoading(true);
    try {
      const result = await getHotelAccounts();
      if (result.success) {
        const hotelData = result.data.data || [];
        setHotels(hotelData);
        setFilteredHotels(hotelData);
        setSnackbar({
          open: true,
          message: hotelData.length === 0 
            ? 'No hotels assigned' 
            : `Loaded ${hotelData.length} hotel account${hotelData.length !== 1 ? 's' : ''} successfully`,
          severity: hotelData.length === 0 ? 'info' : 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to fetch hotel accounts',
          severity: 'error'
        });
        // If auth failure, the API interceptor should handle redirect
        if (result.error?.toLowerCase().includes('token') || result.error?.toLowerCase().includes('auth')) {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch hotel accounts',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch hotel accounts on component mount
  useEffect(() => {
    fetchHotelAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter hotels when search query changes
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredHotels(hotels);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = hotels.filter(hotel => 
        hotel.hotel_id?.toString().includes(query) ||
        hotel.hotel_name?.toLowerCase().includes(query) ||
        hotel.razorpay_account_id?.toLowerCase().includes(query)
      );
      setFilteredHotels(filtered);
    }
  }, [searchQuery, hotels]);

  // Toggle row expansion
  const toggleRowExpansion = (hotelId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(hotelId)) {
      newExpanded.delete(hotelId);
    } else {
      newExpanded.add(hotelId);
    }
    setExpandedRows(newExpanded);
  };

  // Helper function to format boolean values
  const formatValue = (key, value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    // Check if the key suggests it's a boolean field (contains: active, is, enabled, disabled, status)
    const keyLower = key.toLowerCase();
    const isBooleanField = /active|isactive|is_active|enabled|disabled|status/i.test(keyLower);
    
    // If value is 0 or 1, and it's a boolean field, convert to Yes/No
    if (isBooleanField && (value === 0 || value === 1 || value === '0' || value === '1')) {
      return value === 1 || value === '1' ? 'Yes' : 'No';
    }
    
    // Also check if value is 0 or 1 regardless of field name (common boolean representation)
    if (value === 0 || value === 1 || value === '0' || value === '1') {
      // Only convert if field name suggests it might be boolean
      if (isBooleanField || keyLower.includes('flag') || keyLower.includes('bool')) {
        return value === 1 || value === '1' ? 'Yes' : 'No';
      }
    }
    
    // Check if value is boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  // Format object for display
  const formatDetails = (details) => {
    if (!details || typeof details !== 'object') {
      return 'No details available';
    }
    
    return Object.entries(details).map(([key, value]) => (
      <Box key={key} sx={{ mb: 1 }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
        </Typography>{' '}
        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
          {formatValue(key, value)}
        </Typography>
      </Box>
    ));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Hotel Accounts
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHotelAccounts}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage hotel accounts with Razorpay integration details
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '1px solid #90caf9',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {hotels.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            border: '1px solid #ce93d8',
            boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {hotels.filter(h => h.razorpay_account_id).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7b1fa2' }}>With Razorpay Account</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            border: '1px solid #a5d6a7',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SearchIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {filteredHotels.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#388e3c' }}>Filtered Results</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search by Hotel ID, Hotel Name, or Razorpay Account ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Hotel Accounts Table */}
      {filteredHotels.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery 
              ? 'No hotels found matching your search criteria.' 
              : hotels.length === 0 
                ? 'No hotels assigned' 
                : 'No hotels match the search criteria.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Razorpay Account ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Is Active</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057', textAlign: 'center' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHotels.map((hotel) => (
                <React.Fragment key={hotel.hotel_id}>
                  <TableRow 
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(255, 119, 0, 0.04)',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => toggleRowExpansion(hotel.hotel_id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#2E4057' }}>
                        {hotel.hotel_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#2E4057' }}>
                        {hotel.hotel_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {hotel.razorpay_account_id ? (
                        <Chip 
                          label={hotel.razorpay_account_id}
                          size="small"
                          sx={{ 
                            backgroundColor: '#4caf50',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      ) : (
                        <Chip 
                          label="Not Linked"
                          size="small"
                          sx={{ 
                            backgroundColor: '#f44336',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const isActive = hotel.details?.isActive ?? hotel.details?.is_active ?? hotel.isActive ?? hotel.is_active;
                        const activeValue = formatValue('isActive', isActive);
                        const isActiveValue = isActive === 1 || isActive === '1' || isActive === true;
                        return (
                          <Chip 
                            label={activeValue}
                            size="small"
                            sx={{ 
                              backgroundColor: isActiveValue ? '#4caf50' : '#f44336',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small"
                        sx={{ 
                          color: '#FF7700',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 119, 0, 0.1)'
                          }
                        }}
                      >
                        {expandedRows.has(hotel.hotel_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedRows.has(hotel.hotel_id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                          <Typography variant="h6" sx={{ mb: 2, color: '#2E4057', fontWeight: 600 }}>
                            Full Account Details
                          </Typography>
                          <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                            {formatDetails(hotel.details)}
                          </Paper>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelAccounts;

